import { ACSApi } from '../../../api';
import { useContainer } from '../../useContainer';
import { dispatch } from '../../../store';
import { selectMonthlyLogsCache, setMonthlyLogsCacheEntry, setTimeLogsByProjectWithLeaveRequests } from '../../../store/slices/time-tracking.slice';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { DateTime } from 'luxon';
import { TimeLogsByProjectWithLeaveRequestsDto } from '../../../commonTypes/acs/timelog.types';

interface GetLogsOptions {
    preferMonthlyCache?: boolean;
    onSuccess?: (data: TimeLogsByProjectWithLeaveRequestsDto) => void;
}

const SELF_CACHE_KEY = '__self__';

export const createMonthlyLogsCacheKey = (from: string, to: string, userSub?: string) => {
    const fromDate = DateTime.fromISO(from);
    const toDate = DateTime.fromISO(to);
    if (!fromDate.isValid || !toDate.isValid) return null;
    if (!fromDate.hasSame(toDate, 'month')) return null;
    if (fromDate.toISODate() !== fromDate.startOf('month').toISODate()) return null;
    if (toDate.toISODate() !== toDate.endOf('month').toISODate()) return null;

    return `${userSub || SELF_CACHE_KEY}|${fromDate.toFormat('yyyy-MM')}`;
};

const useHttpGetTimeLogsByInterval = () => {
    const acsApi: ACSApi = useContainer(ACSApi);
    const monthlyLogsCache = useSelector(selectMonthlyLogsCache);
    const [isReady, setIsReady] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const getLogs = (from: string, to: string, userSub?: string, options?: GetLogsOptions) => {
        const monthCacheKey = createMonthlyLogsCacheKey(from, to, userSub);
        if (options?.preferMonthlyCache && monthCacheKey && monthlyLogsCache[monthCacheKey]) {
            const cachedData = monthlyLogsCache[monthCacheKey];
            dispatch(setTimeLogsByProjectWithLeaveRequests(cachedData));
            options.onSuccess?.(cachedData);
            setIsReady(true);
            setIsLoading(false);
            return () => undefined;
        }

        setIsLoading(true);
        const { exec, abort } = acsApi.getTimeLogsByInterval({
            query: { from: encodeURIComponent(from), to: encodeURIComponent(to), userSub },
            onSuccess: res => {
                dispatch(setTimeLogsByProjectWithLeaveRequests(res.body));
                if (monthCacheKey) {
                    dispatch(
                        setMonthlyLogsCacheEntry({
                            key: monthCacheKey,
                            value: res.body,
                        })
                    );
                }
                options?.onSuccess?.(res.body);
                setIsReady(true);
                setIsLoading(false);
            },
            onError: error => {
                setIsLoading(false);
                return error;
            },
        });

        exec();
        return abort;
    };

    return { getLogs, isReady, isLoading };
};

export default useHttpGetTimeLogsByInterval;
